// src/hooks/useCanny.ts
import { config } from '@/config';
import { useGetCannyTokenQuery } from '@/redux/query';
import { useEffect, useState } from 'react';

declare global {
  interface Window {
    Canny: (...args: any[]) => void;
  }
}

const BOARD_TOKEN = process.env.REACT_APP_CANNY_BOARD_TOKEN!;
const APP_ID = process.env.REACT_APP_CANNY_APP_ID!;

/**
 * Injects Canny SDK into the page if not already loaded.
 */
const loadCannySdk = () => {
  (function (w: Window & typeof globalThis, d: Document, i: string, s: string) {
    function l() {
      if (!d.getElementById(i)) {
        const f = d.getElementsByTagName(s)[0];
        const e = d.createElement(s) as HTMLScriptElement;
        e.type = 'text/javascript';
        e.async = true;
        e.src = 'https://canny.io/sdk.js';
        f.parentNode!.insertBefore(e, f);
      }
    }
    if (typeof w.Canny !== 'function') {
      const c = function () {
        (c as any).q.push(arguments);
      } as any;
      c.q = [];
      w.Canny = c;
      if (d.readyState === 'complete') {
        l();
      } else {
        w.addEventListener('load', l, false);
      }
    }
  })(window, document, 'canny-jssdk', 'script');
};

/**
 * Render the Canny feedback widget.
 */
export const renderCanny = (token: string, onLoad?: () => void) => {
  if (!window.Canny || !token) return;

  window.Canny('render', {
    boardToken: BOARD_TOKEN,
    basePath: '',
    ssoToken: token,
    theme: 'light',
    onLoadCallback: onLoad,
  });
};

/**
 * Initialize the Canny changelog widget.
 */
export const initCannyChangelog = () => {
  if (!window.Canny) return;

  window.Canny('initChangelog', {
    appID: APP_ID,
    position: 'bottom',
    align: 'right',
    theme: 'light',
  });
};

/**
 * Hook to fetch Canny token and load SDK.
 */
export const useCanny = () => {
  const { data } = useGetCannyTokenQuery({ shop: config.shop });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadCannySdk();
  }, []);

  useEffect(() => {
    if (data?.data?.token) {
      setIsLoading(false);
    }
  }, [data?.data?.token]);

  return {
    isLoadingCanny: isLoading,
    token: data?.data?.token || '',
  };
};
